import React, { useState, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Checkbox,
  FormControlLabel,
  TextField,
  alpha,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import axiosInstance from '../lib/axios';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme } from '../theme/colors';
import { Location } from '../types';
import { Quote, StockCheckResult, TransferRequestDto } from '../types/b2b';

interface AcceptQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  quote: Quote;
  onAccept: (fulfillmentLocationId: string, transferRequests: TransferRequestDto[], notes?: string) => void;
  isAccepting: boolean;
}

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations?limit=100');
  return response.data.data || response.data;
};

const checkStock = async (quoteId: string, locationId: string): Promise<StockCheckResult> => {
  const response = await axiosInstance.get(`/quotes/${quoteId}/check-stock/${locationId}`);
  return response.data;
};

export const AcceptQuoteDialog = ({
  open,
  onClose,
  quote,
  onAccept,
  isAccepting,
}: AcceptQuoteDialogProps) => {
  const { mode } = useContext(ColorModeContext);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [transferRequests, setTransferRequests] = useState<Map<string, TransferRequestDto>>(new Map());
  const [notes, setNotes] = useState('');

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
    enabled: open,
  });

  const { data: stockCheck, isLoading: stockLoading, refetch: refetchStock } = useQuery({
    queryKey: ['quote-stock-check', quote.id, selectedLocationId],
    queryFn: () => checkStock(quote.id, selectedLocationId),
    enabled: open && !!selectedLocationId,
  });

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedLocationId('');
      setTransferRequests(new Map());
      setNotes('');
    }
  }, [open]);

  // Re-check stock when location changes
  useEffect(() => {
    if (selectedLocationId) {
      setTransferRequests(new Map());
      refetchStock();
    }
  }, [selectedLocationId, refetchStock]);

  const handleTransferToggle = (productId: string, checked: boolean, fromLocationId?: string, quantity?: number) => {
    const newMap = new Map(transferRequests);
    if (checked && fromLocationId && quantity) {
      newMap.set(productId, { productId, fromLocationId, quantity });
    } else {
      newMap.delete(productId);
    }
    setTransferRequests(newMap);
  };

  const handleAccept = () => {
    onAccept(selectedLocationId, Array.from(transferRequests.values()), notes || undefined);
  };

  // Aggregate shortfalls by productId to handle multiple line items with same product
  const aggregatedShortfalls = React.useMemo(() => {
    const items = stockCheck?.items || [];
    const aggregated = new Map<string, {
      productId: string;
      productName: string;
      productSku: string | null;
      totalShortfall: number;
      alternativeLocations: Array<{
        locationId: string;
        locationName: string;
        locationType: string;
        available: number;
      }>;
    }>();

    items.forEach(item => {
      if (item.shortfall > 0) {
        const existing = aggregated.get(item.productId);
        if (existing) {
          // Add to existing shortfall total
          existing.totalShortfall += item.shortfall;
        } else {
          // Create new entry
          aggregated.set(item.productId, {
            productId: item.productId,
            productName: item.productName,
            productSku: item.productSku,
            totalShortfall: item.shortfall,
            alternativeLocations: item.alternativeLocations || [],
          });
        }
      }
    });

    return Array.from(aggregated.values());
  }, [stockCheck?.items]);

  const itemsWithShortfall = stockCheck?.items.filter(item => item.shortfall > 0) || [];

  // Check if all aggregated shortfalls are covered by transfers
  const allShortfallsCovered = aggregatedShortfalls.every(item => {
    const transfer = transferRequests.get(item.productId);
    return transfer && transfer.quantity >= item.totalShortfall;
  });

  const canAccept = selectedLocationId && (stockCheck?.canFulfillCompletely || allShortfallsCovered);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: `1px solid ${colors.border}`, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: palette.success }} />
          <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold }}>
            Accept Quote - {quote.quoteNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Location Selector */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
            Select Fulfillment Location
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textMuted }}>
            Choose the location where stock will be reserved and shipped from.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Fulfillment Location</InputLabel>
            <Select
              value={selectedLocationId}
              label="Fulfillment Location"
              onChange={(e) => setSelectedLocationId(e.target.value)}
              disabled={locationsLoading}
            >
              {locations?.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: location.type === 'warehouse' ? palette.info : palette.success }} />
                    <span>{location.name}</span>
                    <Chip
                      label={location.type}
                      size="small"
                      sx={{ ml: 1, fontSize: 10, height: 20 }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Stock Check Results */}
        {selectedLocationId && (
          <Box sx={{ mb: 3 }}>
            {stockLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: palette.primary }} />
              </Box>
            ) : stockCheck ? (
              <>
                {/* Status Summary */}
                <Alert
                  severity={stockCheck.canFulfillCompletely ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                  icon={stockCheck.canFulfillCompletely ? <CheckCircleIcon /> : <WarningIcon />}
                >
                  {stockCheck.canFulfillCompletely ? (
                    <Typography>
                      All items can be fulfilled from <strong>{stockCheck.fulfillmentLocationName}</strong>
                    </Typography>
                  ) : (
                    <Typography>
                      <strong>{stockCheck.totalShortfall}</strong> units across <strong>{itemsWithShortfall.length}</strong> items are not available at {stockCheck.fulfillmentLocationName}
                    </Typography>
                  )}
                </Alert>

                {/* Stock Availability Table */}
                <Typography variant="subtitle2" sx={{ mb: 1, color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                  Stock Availability
                </Typography>
                <TableContainer sx={{ border: `1px solid ${colors.border}`, borderRadius: theme.radius.sm, mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(colors.border, 0.5) }}>
                        <TableCell sx={{ fontWeight: typography.fontWeight.bold }}>Product</TableCell>
                        <TableCell align="center" sx={{ fontWeight: typography.fontWeight.bold }}>Requested</TableCell>
                        <TableCell align="center" sx={{ fontWeight: typography.fontWeight.bold }}>Available</TableCell>
                        <TableCell align="center" sx={{ fontWeight: typography.fontWeight.bold }}>Shortfall</TableCell>
                        <TableCell align="center" sx={{ fontWeight: typography.fontWeight.bold }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockCheck.items.map((item) => (
                        <TableRow key={item.productId}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: typography.fontWeight.medium }}>
                              {item.productName}
                            </Typography>
                            {item.productSku && (
                              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                SKU: {item.productSku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.requestedQty}</TableCell>
                          <TableCell align="center">{item.availableAtLocation}</TableCell>
                          <TableCell align="center">
                            {item.shortfall > 0 ? (
                              <Typography sx={{ color: palette.error, fontWeight: typography.fontWeight.bold }}>
                                {item.shortfall}
                              </Typography>
                            ) : (
                              <Typography sx={{ color: colors.textMuted }}>-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {item.shortfall > 0 ? (
                              <Chip
                                label="Shortfall"
                                size="small"
                                sx={{ backgroundColor: alpha(palette.error, 0.1), color: palette.error, fontSize: 10 }}
                              />
                            ) : (
                              <Chip
                                label="OK"
                                size="small"
                                sx={{ backgroundColor: alpha(palette.success, 0.1), color: palette.success, fontSize: 10 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Transfer Requests Section - Uses aggregated shortfalls to handle duplicate products */}
                {aggregatedShortfalls.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <LocalShippingIcon sx={{ color: palette.info }} />
                      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                        Request Stock Transfers
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: colors.textMuted }}>
                      Select items to transfer from other locations to cover shortfalls.
                    </Typography>

                    {aggregatedShortfalls.map((item) => (
                      <Box
                        key={item.productId}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: `1px solid ${colors.border}`,
                          borderRadius: theme.radius.sm,
                          backgroundColor: transferRequests.has(item.productId)
                            ? alpha(palette.success, 0.1)
                            : alpha(colors.border, 0.2),
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: typography.fontWeight.bold }}>
                            {item.productName}
                          </Typography>
                          <Chip
                            label={`Need ${item.totalShortfall} units`}
                            size="small"
                            sx={{ backgroundColor: alpha(palette.error, 0.1), color: palette.error }}
                          />
                        </Box>

                        {item.alternativeLocations.length > 0 ? (
                          <>
                            <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
                              Available at other locations:
                            </Typography>
                            {item.alternativeLocations.map((altLoc) => {
                              const isSelected = transferRequests.has(item.productId) &&
                                transferRequests.get(item.productId)?.fromLocationId === altLoc.locationId;
                              const canCoverShortfall = altLoc.available >= item.totalShortfall;

                              return (
                                <FormControlLabel
                                  key={altLoc.locationId}
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={(e) => handleTransferToggle(
                                        item.productId,
                                        e.target.checked,
                                        altLoc.locationId,
                                        // Use the total aggregated shortfall, capped by available
                                        Math.min(item.totalShortfall, altLoc.available)
                                      )}
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <span>{altLoc.locationName}</span>
                                      <Chip
                                        label={altLoc.locationType}
                                        size="small"
                                        sx={{ fontSize: 9, height: 16 }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{ color: canCoverShortfall ? palette.success : palette.warning }}
                                      >
                                        ({altLoc.available} available)
                                      </Typography>
                                      {!canCoverShortfall && (
                                        <Typography variant="caption" sx={{ color: palette.warning }}>
                                          - partial coverage
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ mb: 0.5 }}
                                />
                              );
                            })}
                          </>
                        ) : (
                          <Alert severity="error" sx={{ py: 0.5 }}>
                            No alternative locations have this item in stock
                          </Alert>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : null}
          </Box>
        )}

        {/* Notes */}
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this acceptance..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${colors.border}` }}>
        <Button onClick={onClose} disabled={isAccepting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!canAccept || isAccepting}
          sx={{ backgroundColor: palette.success, '&:hover': { backgroundColor: alpha(palette.success, 0.9) } }}
        >
          {isAccepting ? 'Accepting...' : 'Accept Quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
